import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Query.css';

const API = '/api/v2';
const OPS_NUM = [
  { value: 'between', label: 'Between' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'eq', label: '=' },
];
const OPS_CAT = [
  { value: 'eq', label: 'Is' },
  { value: 'neq', label: 'Is not' },
  { value: 'in', label: 'In' },
];
const OPS_TEXT = [
  { value: 'contains', label: 'Contains' },
  { value: 'eq', label: 'Exact' },
];
const OPS_BOOL = [
  { value: 'eq', label: 'Is' },
];
const OPS_EXIST = [
  { value: 'notnull', label: 'Has value' },
  { value: 'isnull', label: 'Is empty' },
];

function Query() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [schema, setSchema] = useState([]);
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);
  const [visibleCols, setVisibleCols] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  useEffect(() => {
    axios.get(API + '/campaigns').then(r => {
      const c = r.data.campaigns || [];
      setCampaigns(c);
      if (c.length) handleSelectCampaign(c[0].slug);
    });
  }, []);

  const handleSelectCampaign = async (slug) => {
    setSelectedSlug(slug);
    setResults(null);
    setFilters([]);
    setPage(0);
    try {
      const r = await axios.get(API + '/campaigns/' + slug);
      const f = r.data.fields || [];
      setSchema(f);
      const defaultCols = f.filter(fi => fi.is_required || ['sex','age_at_surgery','bmi','approach','preop_odi','preop_vas_back'].includes(fi.field_name)).map(fi => fi.field_name).slice(0, 10);
      setVisibleCols(defaultCols.length ? defaultCols : f.slice(0, 8).map(fi => fi.field_name));
      try { const fv = await axios.get(API + '/campaigns/' + slug + '/field-values'); setFieldValues(fv.data.values || {}); } catch(e) {}
    } catch (e) { console.error(e); }
  };

  const addFilter = () => {
    const first = schema[0];
    if (!first) return;
    const isNum = ['integer','float'].includes(first.data_type);
    setFilters([...filters, { field: first.field_name, op: isNum ? 'between' : 'eq', value: '', min: '', max: '' }]);
  };

  const updateFilter = (i, updates) => {
    const f = [...filters];
    f[i] = { ...f[i], ...updates };
    if (updates.field) {
      const fd = schema.find(s => s.field_name === updates.field);
      if (fd && ['integer','float'].includes(fd.data_type)) f[i].op = 'between';
      else if (fd && fd.data_type === 'categorical') f[i].op = 'eq';
      else if (fd && fd.data_type === 'boolean') f[i].op = 'eq';
      else f[i].op = 'contains';
      f[i].value = ''; f[i].min = ''; f[i].max = '';
    }
    setFilters(f);
  };

  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));

  const runQuery = async (newPage) => {
    const p = newPage !== undefined ? newPage : page;
    setLoading(true); setError('');
    try {
      const r = await axios.post(API + '/campaigns/' + selectedSlug + '/query', {
        filters, sort_field: sortField || undefined, sort_dir: sortDir, limit, offset: p * limit
      });
      setResults(r.data);
      setPage(p);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setTimeout(() => runQuery(0), 50);
  };

  const toggleCol = (fname) => {
    setVisibleCols(prev => prev.includes(fname) ? prev.filter(c => c !== fname) : [...prev, fname]);
  };

  const exportExcel = () => {
    if (!selectedSlug) return;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) { alert('Please log in to export'); return; }
    window.open(API + '/query/export?campaign=' + selectedSlug + '&token=' + token, '_blank');
  };

  const exportCSV = () => {
    if (!results?.patients?.length) return;
    const cols = ['arm'].concat(visibleCols);
    const labels = ['Arm'].concat(visibleCols.map(c => { const f = schema.find(s => s.field_name === c); return f ? f.field_label : c; }));
    const header = labels.join(',');
    const rows = results.patients.map(p => {
      return [p.arm_name || ''].concat(visibleCols.map(c => {
        const v = p.data?.[c];
        if (v === null || v === undefined) return '';
        return String(v).includes(',') ? '"' + v + '"' : String(v);
      })).join(',');
    });
    const csv = header + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'query-' + selectedSlug + '.csv'; a.click();
  };

  const getOps = (field) => {
    const fd = schema.find(s => s.field_name === field);
    if (!fd) return OPS_TEXT;
    if (['integer','float'].includes(fd.data_type)) return [...OPS_NUM, ...OPS_EXIST];
    if (fd.data_type === 'categorical') return [...OPS_CAT, ...OPS_EXIST];
    if (fd.data_type === 'boolean') return [...OPS_BOOL, ...OPS_EXIST];
    return [...OPS_TEXT, ...OPS_EXIST];
  };

  const getFieldDef = (fname) => schema.find(s => s.field_name === fname);

  const categories = [...new Set(schema.map(f => f.category))];
  const totalPages = results ? Math.ceil(results.total / limit) : 0;

  return (
    <div className="query-container">
      <div className="query-header">
        <h1>Query Patient Data</h1>
        <select className="campaign-picker" value={selectedSlug} onChange={e => handleSelectCampaign(e.target.value)}>
          {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name} ({c.patient_count})</option>)}
        </select>
      </div>

      <div className="query-body">
        <div className="filters-panel">
          <div className="fp-header">
            <h3>Filters</h3>
            <button className="btn-add-filter" onClick={addFilter}>+ Add Filter</button>
          </div>

          {filters.length === 0 && <p className="fp-hint">No filters — query returns all patients. Add filters to narrow results.</p>}

          {filters.map((f, i) => {
            const fd = getFieldDef(f.field);
            const ops = getOps(f.field);
            return (
              <div key={i} className="filter-row">
                <select className="fr-field" value={f.field} onChange={e => updateFilter(i, { field: e.target.value })}>
                  {categories.map(cat => (
                    <optgroup key={cat} label={cat.replace(/_/g,' ').replace(/^\w/,c=>c.toUpperCase())}>
                      {schema.filter(s => s.category === cat).map(s => (
                        <option key={s.field_name} value={s.field_name}>{s.field_label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <select className="fr-op" value={f.op} onChange={e => updateFilter(i, { op: e.target.value })}>
                  {ops.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {f.op === 'between' ? (
                  <div className="fr-range">
                    <input type="number" placeholder="Min" value={f.min} onChange={e => updateFilter(i, { min: e.target.value })} />
                    <span>to</span>
                    <input type="number" placeholder="Max" value={f.max} onChange={e => updateFilter(i, { max: e.target.value })} />
                  </div>
                ) : f.op === 'notnull' || f.op === 'isnull' ? null : (
                  fd?.data_type === 'categorical' ? (
                    <select className="fr-value" value={f.value} onChange={e => updateFilter(i, { value: e.target.value })}>
                      <option value="">Select...</option>
                      {(fieldValues[f.field] || (fd.options ? (typeof fd.options === 'string' ? JSON.parse(fd.options) : fd.options) : [])).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : fd?.data_type === 'boolean' ? (
                    <select className="fr-value" value={f.value} onChange={e => updateFilter(i, { value: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input className="fr-value" type={['integer','float'].includes(fd?.data_type) ? 'number' : 'text'} value={f.value} placeholder="Value..." onChange={e => updateFilter(i, { value: e.target.value })} />
                  )
                )}
                <button className="fr-remove" onClick={() => removeFilter(i)}>&times;</button>
              </div>
            );
          })}

          <div className="fp-actions">
            <button className="btn-run" onClick={() => runQuery(0)} disabled={loading}>{loading ? 'Querying...' : 'Run Query'}</button>
            <select className="limit-picker" value={limit} onChange={e => setLimit(parseInt(e.target.value))}>
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
              <option value={250}>250 rows</option>
            </select>
          </div>
        </div>

        {error && <div className="query-error">{error}</div>}

        {results && (
          <div className="results-panel">
            <div className="rp-header">
              <div className="rp-count">
                <strong>{results.total}</strong> patients found
                {totalPages > 1 && <span className="rp-page"> · page {page + 1}/{totalPages}</span>}
              </div>
              <div className="rp-actions">
                <button className="btn-export btn-export-xlsx" onClick={exportExcel}>Export Excel</button>
                <button className="btn-export" onClick={exportCSV}>Export CSV</button>
              </div>
            </div>

            {results.summary && Object.keys(results.summary).length > 1 && (
              <div className="summary-cards">
                <div className="sc"><span className="sc-val">{results.summary.count}</span><span className="sc-label">Matching</span></div>
                {schema.filter(f => results.summary['avg_' + f.field_name] != null).slice(0, 6).map(f => (
                  <div key={f.field_name} className="sc">
                    <span className="sc-val">{results.summary['avg_' + f.field_name]}</span>
                    <span className="sc-label">Avg {f.field_label}</span>
                    <span className="sc-range">{results.summary['min_' + f.field_name]}–{results.summary['max_' + f.field_name]}</span>
                  </div>
                ))}
              </div>
            )}

            {results.distributions && Object.keys(results.distributions).length > 0 && (
              <div className="dist-section">
                {Object.entries(results.distributions).map(([fname, vals]) => {
                  const fd = schema.find(s => s.field_name === fname);
                  const total = vals.reduce((s, v) => s + parseInt(v.count), 0);
                  return (
                    <div key={fname} className="dist-card">
                      <h4>{fd?.field_label || fname}</h4>
                      {vals.map(v => (
                        <div key={v.val} className="dist-row">
                          <span className="dr-label">{v.val}</span>
                          <div className="dr-bar"><div className="dr-fill" style={{ width: (parseInt(v.count)/total*100) + '%' }}></div></div>
                          <span className="dr-count">{v.count}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="col-picker">
              <span className="cp-label">Columns:</span>
              <div className="cp-chips">
                {schema.slice(0, 30).map(f => (
                  <button key={f.field_name} className={'cp-chip ' + (visibleCols.includes(f.field_name) ? 'active' : '')}
                    onClick={() => toggleCol(f.field_name)}>{f.field_label}</button>
                ))}
              </div>
            </div>

            <div className="results-table-wrap">
              <table className="results-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('arm')}>Arm {sortField === 'arm' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                    {visibleCols.map(c => {
                      const fd = schema.find(s => s.field_name === c);
                      return <th key={c} className="sortable" onClick={() => handleSort(c)}>{fd?.field_label || c} {sortField === c ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>;
                    })}
                    <th>Comp.</th>
                  </tr>
                </thead>
                <tbody>
                  {results.patients.map(p => (
                    <tr key={p.id}>
                      <td><span className="arm-badge">{p.arm_name || '-'}</span></td>
                      {visibleCols.map(c => <td key={c}>{p.data?.[c] ?? '-'}</td>)}
                      <td>{Math.round(p.completeness * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page === 0} onClick={() => runQuery(page - 1)}>← Previous</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={() => runQuery(page + 1)}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default Query;
